<script setup lang="ts">
import { useHead } from '#app';
import AppNav from '@/components/AppNav.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import AppFooter from '@/components/AppFooter.vue';
import { t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/solutions'));
const solutionPath = (slug: string) => localizedPath(siteLocale.value, `/solutions/${slug}`);

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: t('landingPages.solutions.index.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: t('landingPages.solutions.index.seo.description') },
    { name: 'keywords', content: t('landingPages.solutions.index.seo.keywords') },
    { property: 'og:title', content: t('landingPages.solutions.index.seo.title') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    {
      property: 'og:description',
      content: t('landingPages.solutions.index.seo.description'),
    },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: t('landingPages.solutions.index.seo.title') },
    {
      name: 'twitter:description',
      content: t('landingPages.solutions.index.seo.description'),
    },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }, ...buildPathHreflangLinks('/solutions')],
}));

const solutionDefs = [
  {
    slug: 'for-marketers',
    cardKey: 'marketers',
    image: 'https://static.veeso.ai/static-assets/marketer.svg',
  },
  {
    slug: 'for-human-resource',
    cardKey: 'hr',
    image: 'https://static.veeso.ai/static-assets/hr-people.svg',
  },
  {
    slug: 'for-founders',
    cardKey: 'founders',
    image: 'https://static.veeso.ai/static-assets/founder.svg',
  },
  {
    slug: 'for-creators',
    cardKey: 'creators',
    image: 'https://static.veeso.ai/static-assets/content-creator.svg',
  },
  {
    slug: 'for-startups',
    cardKey: 'startups',
    image: 'https://static.veeso.ai/static-assets/startups.svg',
  },
] as const;

const solutions = computed(() =>
  solutionDefs.map((def) => ({
    slug: def.slug,
    image: def.image,
    title: t(`landingPages.solutions.index.card.${def.cardKey}.title`),
    description: t(`landingPages.solutions.index.card.${def.cardKey}.description`),
  }))
);
</script>

<template>
  <div class="min-h-screen relative flex flex-col transition-colors duration-300">
    <AppNav active-path="/solutions" />

    <main class="relative z-[1] pt-[72px]">
      <!-- Hero Section -->
      <section class="py-24 px-6 md:px-12 max-w-[1320px] mx-auto" aria-label="hero-section">
        <div class="w-full md:w-[50%] text-left">
          <h1 class="text-3xl md:text-5xl font-bold text-black/90 tracking-tight mb-2">
            {{ t('landingPages.solutions.index.hero.title') }}
          </h1>
          <p class="text-xl md:text-2xl leading-relaxed text-black/60 font-normal">
            {{ t('landingPages.solutions.index.hero.subtitle') }}
          </p>
        </div>
      </section>

      <!-- Solutions Grid -->
      <section class="max-w-[1320px] mx-auto px-4 md:px-8 pb-24" aria-label="solutions-grid">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <NuxtLink
            v-for="solution in solutions"
            :key="solution.slug"
            :to="solutionPath(solution.slug)"
            class="flex flex-col p-2 rounded-2xl bg-white overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02]"
          >
            <div
              class="aspect-square w-full rounded-xl overflow-hidden mb-4 bg-black/5 flex items-center justify-center"
            >
              <img :src="solution.image" :alt="solution.title" class="w-full h-full object-cover" />
            </div>
            <h2 class="text-xl md:text-2xl font-medium text-black/90 px-4">
              {{ solution.title }}
            </h2>
            <p class="text-base text-black/60 font-normal mb-4 px-4">
              {{ solution.description }}
            </p>
            <span
              class="text-base font-medium text-black/80 mt-auto inline-flex items-center gap-2 p-4"
            >
              {{ t('landingPages.solutions.index.card.learn_more') }}
              <span class="transition-transform group-hover:translate-x-1">→</span>
            </span>
          </NuxtLink>
        </div>
      </section>

      <!-- CTA Section -->
      <FeaturesRoles />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
/* 可以添加自定义样式 */
</style>
